import { getMeta, setMeta } from './localdb.js';

/**
 * Talking to the server.
 *
 * Small on purpose. The client has exactly two conversations with the network — refill the
 * local database, drain the queue — and neither is on the path of anything a technician is
 * looking at. Nothing in a screen calls this directly.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
  }

  /**
   * Whether retrying could ever help.
   *
   * The distinction the outbox lives on. A 4xx means the request itself is wrong and will be
   * wrong again — with two exceptions: 401 is a session that expired and will work once signed
   * in, and 429 is a queue asking to be slower.
   */
  get permanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 401 && this.status !== 429;
  }
}

/**
 * The business in the URL.
 *
 * Every path is `/api/{slug}/…` because the slug is what the server validates the session
 * against. It is learned once at sign-in and kept, so the app can build a path with no signal.
 */
export async function tenantSlug(): Promise<string | null> {
  return getMeta('tenantSlug');
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const slug = await tenantSlug();
  const url = path.startsWith('/auth/') || path.startsWith('/me/') || !slug ? `/api${path}` : `/api/${slug}${path}`;
  return fetch(url, { credentials: 'same-origin', ...init });
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiFetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      (body as { error?: string } | null)?.error ?? `http_${response.status}`,
      (body as { message?: string } | null)?.message ?? `HTTP ${response.status}`,
      body,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export type Session = {
  userId: string;
  email: string;
  name: string;
  locale: 'fr' | 'en';
  tenantId: string;
  tenantSlug: string | null;
  tenantName: string;
  role: { id: string; key: string; name: unknown; landing: 'portal' | 'field' };
  capabilities: string[];
};

/**
 * Who is signed in, and where.
 *
 * The one call the app makes before it can do anything else, and the only one whose failure is
 * allowed to stop it: without a business in hand there is no path to build. Its answer is
 * cached in the local database so a cold start in a basement still knows who it belongs to.
 */
export async function loadSession(): Promise<Session | null> {
  try {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (!response.ok) return null;
    const session = (await response.json()) as Session;
    if (session.tenantSlug) await setMeta('tenantSlug', session.tenantSlug);
    await setMeta('userId', session.userId);
    await setMeta('tenantId', session.tenantId);
    await setMeta('capabilities', JSON.stringify(session.capabilities));
    return session;
  } catch {
    return null;
  }
}

/** The capabilities this account holds, from the last time the server said. */
export async function heldCapabilities(): Promise<Set<string>> {
  const raw = await getMeta('capabilities');
  if (!raw) return new Set();
  try {
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}
